import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useEditSegmentView } from "../EditSegment.context";
import { ConditionGroupProps } from "./ConditionGroup";

export function useConditionGroup(props: ConditionGroupProps) {
  const {
    index,
    onEmpty,
    prefixName,
    hideDivider,
    attributesTypes,
    maxConditions,
    hideAddConditionsAtMax,
  } = props;

  const { segmentConditionsInfo, editDisabled, editDisabledTooltip } =
    useEditSegmentView();

  const { fields, append, remove } = useFieldArray({
    name: `${prefixName}.conditions`,
  });

  const { trigger, watch } = useFormContext();
  const groupConditions = watch(`${prefixName}.conditions`);

  const addCondition = React.useCallback(() => {
    append({
      type: "base",
      attributeType: "",
      attributeName: "",
      criteria: "",
      value: {},
    });
    trigger();
  }, [append, trigger]);

  const deleteCondition = React.useCallback(
    (i: number) => {
      remove(i);
    },
    [remove]
  );

  React.useEffect(() => {
    if (groupConditions && Array.isArray(groupConditions)) {
      const hasActivityCondition = groupConditions.some(
        (cond: any) => cond?.attributeType === "ACTIVITY"
      );
      const hasClickedCondition = groupConditions.some(
        (cond: any) =>
          cond?.attributeType === "ACTIVITY" &&
          cond?.attributeName === "Clicked"
      );

      if (!hasActivityCondition) {
        const senderIndices = groupConditions
          .map((cond: any, idx: number) =>
            cond?.attributeType === "SENDER" ? idx : -1
          )
          .filter((idx: number) => idx !== -1)
          .reverse();

        senderIndices.forEach((idx: number) => {
          remove(idx);
        });
      }

      if (!hasClickedCondition) {
        const linkUrlIndices = groupConditions
          .map((cond: any, idx: number) =>
            cond?.attributeType === "LINK_URL" ? idx : -1
          )
          .filter((idx: number) => idx !== -1)
          .reverse();

        linkUrlIndices.forEach((idx: number) => {
          remove(idx);
        });
      }
    }
  }, [groupConditions, remove]);

  React.useEffect(() => {
    if (fields.length === 0) {
      onEmpty?.(index);
    }
  }, [fields.length, index, onEmpty]);

  return {
    addCondition,
    deleteCondition,
    index,
    segmentConditionsInfo,
    fields,
    prefixName,
    hideDivider,
    attributesTypes,
    editDisabled,
    editDisabledTooltip,
    maxConditions,
    hideAddConditionsAtMax,
  };
}
